import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import type { FundSource } from "schema/gen/es/chiikipoint/model/v2/model_pb";
import Close from "~icons/material-symbols/close";

import { css } from "../../../../styled-system/css";
import { trimPrefecture } from "../../../libs/utils";

import { t } from "../../../libs/i18n";
import { selectButtonStyle } from "../constants";
import type { StoreSearchFilters } from "../types";

type SearchModalProps = {
  cities: string[];
  defaultCity: string;
  categories: string[];
  availableFundSources: FundSource[];
  isOpen: boolean;
  onClose: () => void;
  onSearch: (result: StoreSearchFilters) => void;
  shouldFocusInput: boolean;
  selectedCities: string[];
  selectedCategories: string[];
  selectedFundSourceIds: string[];
};

export function SearchModal({
  cities,
  defaultCity,
  categories,
  availableFundSources,
  isOpen,
  onClose,
  onSearch,
  shouldFocusInput,
  selectedCities: selectedCitiesProp,
  selectedCategories: selectedCategoriesProp,
  selectedFundSourceIds: selectedFundSourceIdsProp,
}: SearchModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchText, setSearchText] = useState("");
  const [selectedCities, setSelectedCities] = useState(selectedCitiesProp);
  const [selectedCategories, setSelectedCategories] = useState(
    selectedCategoriesProp,
  );
  const [selectedFundSourceIds, setSelectedFundSourceIds] = useState(
    selectedFundSourceIdsProp,
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedCities(selectedCitiesProp);
      setSelectedCategories(selectedCategoriesProp);
      setSelectedFundSourceIds(selectedFundSourceIdsProp);
    }
  }, [
    isOpen,
    selectedCitiesProp,
    selectedCategoriesProp,
    selectedFundSourceIdsProp,
  ]);

  useEffect(() => {
    if (isOpen && shouldFocusInput && inputRef.current) {
      inputRef.current.focus();

      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 200);
    }
  }, [isOpen, shouldFocusInput]);

  if (!isOpen) return null;

  const handleCityChange = (city: string) => {
    const newSelectedCities = selectedCities.includes(city)
      ? selectedCities.filter((c) => c !== city)
      : [...selectedCities, city];
    setSelectedCities(newSelectedCities);
  };

  const handleCategoryChange = (category: string) => {
    const newSelectedCategories = selectedCategories.includes(category)
      ? selectedCategories.filter((c) => c !== category)
      : [...selectedCategories, category];
    setSelectedCategories(newSelectedCategories);
  };

  const handleFundSourceChange = (fundSourceId: string) => {
    const newSelectedFundSourceIds = selectedFundSourceIds.includes(
      fundSourceId,
    )
      ? selectedFundSourceIds.filter((id) => id !== fundSourceId)
      : [...selectedFundSourceIds, fundSourceId];
    setSelectedFundSourceIds(newSelectedFundSourceIds);
  };

  const handleSearch = () => {
    onSearch({
      text: searchText,
      categories: selectedCategories,
      cities: selectedCities,
      selectedFundSourceIds,
    });
  };

  const handleClear = () => {
    setSearchText("");
    setSelectedCities([defaultCity]);
    setSelectedCategories([]);
    setSelectedFundSourceIds([]);
  };

  return (
    <div
      className={css({
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        animation: "slide-up 0.2s ease-out",
        display: "flex",
        flexDirection: "column",
        pt: "16px",
      })}
    >
      <div
        className={css({
          bg: "background.background",
          rounded: "xl",
          borderTop: "1px solid",
          borderColor: "border.secondary",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
        })}
      >
        <header
          className={css({
            h: "48px",
            justifyContent: "center",
            alignItems: "center",
            display: "flex",
            position: "sticky",
            top: 0,
            bg: "background.background",
            zIndex: 1,
            color: "text.primary",
            roundedTop: "xl",
          })}
        >
          <h1
            className={css({
              color: "text.primary",
              fontWeight: "500",
              fontSize: "18px",
            })}
          >
            {t("条件で探す")}
          </h1>
          <button
            type="button"
            className={css({
              position: "absolute",
              right: 0,
              top: 0,
              height: "48px",
              width: "48px",
              pr: "16px",
              textAlign: "center",
            })}
            onClick={onClose}
          >
            <Close className={css({ width: "24px", height: "24px" })} />
          </button>
        </header>
        <div
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            pt: "16px",
            pb: "calc(env(safe-area-inset-bottom, 16px) + 128px)",
          })}
        >
          <div
            className={css({
              display: "flex",
              gap: "16px",
              px: "16px",
            })}
          >
            <input
              ref={inputRef}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              type="text"
              placeholder={t("店舗名で探す")}
              className={css({
                display: "flex",
                alignItems: "center",
                gap: "8px",
                bg: "white",
                rounded: "xl",
                py: "8px",
                px: "16px",
                flexGrow: 1,
                color: "text.secondary",
                border: "1px solid",
                borderColor: "border.secondary",
              })}
            />

            <button
              type="button"
              className={css({
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: "text.secondary",
              })}
              onClick={handleClear}
            >
              {t("クリア")}
            </button>
          </div>

          <div
            className={css({
              px: "16px",
              color: "text.secondary",
              fontSize: "12px",
            })}
          >
            {t(
              "店舗名での検索時は、選択されたカテゴリやエリアの条件は適用されません。カテゴリやエリアの条件で店舗を探す場合は、店舗名を空欄にしてください。",
            )}
          </div>

          <div
            className={css({
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              px: "16px",
            })}
          >
            <h3>{t("カテゴリ選択")}</h3>
            <div
              className={css({
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
              })}
            >
              {categories.map((category) => (
                <button
                  type="button"
                  className={clsx(
                    css(selectButtonStyle),
                    selectedCategories.includes(category) && "active",
                  )}
                  key={category}
                  onClick={() => handleCategoryChange(category)}
                >
                  <span>{category}</span>
                </button>
              ))}
            </div>
          </div>

          {cities.length > 1 && (
            <>
              <hr
                className={css({
                  border: "1px solid",
                  borderColor: "border.secondary",
                })}
              />
              <div
                className={css({
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  px: "16px",
                })}
              >
                <h3>{t("市区町村で絞る")}</h3>
                <div
                  className={css({
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                  })}
                >
                  {cities.map((city) => (
                    <label
                      key={city}
                      className={css({
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                        width: "calc(50% - 4px)",
                      })}
                    >
                      <input
                        type="checkbox"
                        value={city}
                        checked={selectedCities.includes(city)}
                        onChange={() => handleCityChange(city)}
                      />
                      <span>{trimPrefecture(city)}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}

          {availableFundSources.length > 0 && (
            <>
              <hr
                className={css({
                  border: "1px solid",
                  borderColor: "border.secondary",
                })}
              />
              <div
                className={css({
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  px: "16px",
                })}
              >
                <div
                  className={css({
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  })}
                >
                  <div
                    className={css({
                      width: "18px",
                      height: "18px",
                      rounded: "full",
                      border: "1px solid",
                      borderColor: "border.primary",
                      color: "text.primary",
                      fontSize: "10px",
                      fontWeight: "700",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    })}
                  >
                    P
                  </div>
                  <h3>{t("使えるポイントで絞る")}</h3>
                </div>
                <div
                  className={css({
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  })}
                >
                  {availableFundSources.map((fundSource) => (
                    <label
                      key={fundSource.id}
                      className={css({
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                      })}
                    >
                      <input
                        type="checkbox"
                        value={fundSource.id}
                        checked={selectedFundSourceIds.includes(fundSource.id)}
                        onChange={() => handleFundSourceChange(fundSource.id)}
                      />
                      <span>{fundSource.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <div
        className={css({
          bg: "white",
          position: "fixed",
          bottom: 0,
          width: "100%",
          pt: "16px",
          pb: "calc(env(safe-area-inset-bottom, 16px) + 28px)",
          px: "16px",
          display: "flex",
          justifyContent: "center",
          borderTop: "1px solid",
          borderColor: "border.secondary",
        })}
      >
        <button
          type="button"
          className={css({
            bg: "surface.accentPrimary",
            color: "white",
            rounded: "xl",
            py: "12px",
            px: "16px",
            width: "100%",
          })}
          onClick={handleSearch}
        >
          {t("検索する")}
        </button>
      </div>
    </div>
  );
}
